import React from 'react';
import './PolicyPage.css';

const widthCss = 'mx-8 xl:mx-auto xl:w-lg';

type Props = {
  children: React.ReactNode;
};
function PolicyPage({ children }: Props): JSX.Element {
  return (
    <div style={{ minHeight: '100vh' }} className="policy-page flex flex-column">
      {children}
    </div>
  );
}

type LogoProps = {
  children: React.ReactNode;
};
function Logo({ children }: LogoProps) {
  return <div className="xl:sticky top-0 ml-8 pt-8">{children}</div>;
}

type TitleProps = {
  children: React.ReactNode;
};
function Title({ children }: TitleProps) {
  return <h1 className={`text-5xl font-normal mb-8 ${widthCss}`}>{children}</h1>;
}

type BodyProps = {
  children: React.ReactNode;
  banner?: React.ReactNode;
};
function Body({ children, banner }: BodyProps) {
  return (
    <div className={`policy-body relative rounded-2 bg-white flex-1 mb-6 py-4 px-6 text-lighter ${widthCss}`}>
      {banner}
      <div className="flex gap-x-6">{children}</div>
    </div>
  );
}

type ContentProps = {
  children: React.ReactNode;
};
function Content({ children }: ContentProps) {
  return <div className="policy-content flex-1">{children}</div>;
}

type TocProps = {
  children: React.ReactNode;
};
function TOC({ children }: TocProps) {
  return (
    <div className="sticky top-0 self-start pt-4">
      <ol className="policy-toc nowrap">{children}</ol>
    </div>
  );
}

type TocLinkProps = {
  id: string;
  label: string;
};
function TOCLink({ id, label }: TocLinkProps) {
  return (
    <li className="policy-toc-item overflow-ellipses" style={{ width: '12vw' }}>
      <a className="policy-toc-link font-bold text-interactive hover:text-brand hover:underline" href={`#${id}`}>
        {label}
      </a>
    </li>
  );
}

type ChaptersProps = {
  children: React.ReactNode;
};
function Chapters({ children }: ChaptersProps) {
  return (
    <ol data-test-id="policy-chapters" className="policy-chapters">
      {children}
    </ol>
  );
}

type ChapterProps = {
  id: string;
  title: string;
  children: React.ReactNode;
};
function Chapter({ id, title, children }: ChapterProps) {
  return (
    <li className="policy-chapter">
      <h2 id={id} className="policy-chapter-title font-normal text-3xl text-default mb-2 mt-8">
        {title}
      </h2>
      {children}
    </li>
  );
}

type SubChaptersProps = {
  children: React.ReactNode;
};
function SubChapters({ children }: SubChaptersProps) {
  return <ol className="policy-sub-chapters">{children}</ol>;
}

type SubChapterProps = {
  title?: string;
  children: React.ReactNode;
};
function SubChapter({ title, children }: SubChapterProps) {
  return (
    <li className={`policy-sub-chapter ${!title ? 'policy-sub-chapter--no-title' : ''} mb-3`}>
      {title && <h3 className="policy-sub-chapter-title font-normal text-xl text-default mt-3 mb-3">{title}</h3>}
      {children}
    </li>
  );
}

PolicyPage.Logo = Logo;
PolicyPage.Title = Title;
PolicyPage.Body = Body;
PolicyPage.Content = Content;
PolicyPage.TOC = TOC;
PolicyPage.TOCLink = TOCLink;
PolicyPage.Chapters = Chapters;
PolicyPage.Chapter = Chapter;
PolicyPage.SubChapters = SubChapters;
PolicyPage.SubChapter = SubChapter;

export { PolicyPage };
